import React, { useMemo } from 'react';
import { accountUniversalWrapperDefaultProps, accountUniversalWrapperPropTypes } from './props';

/**
 * AccountUniversalWrapper
 * ----------------------
 * A flexible, responsive wrapper component that sets padding and margin dynamically
 * using CSS variables. Supports base, per-side, and breakpoint-specific props.
 *
 * Props:
 * - padding, margin (string): Base all-sides spacing
 * - paddingTop, paddingRight, paddingBottom, paddingLeft,
 *   marginTop, marginRight, marginBottom, marginLeft: per-side overrides
 * - paddingMd, paddingLg, marginMd, marginLg, etc.: responsive overrides
 * - className (string): Optional additional CSS class for custom styling
 * - children: Content to render inside the wrapper
 *
 * Behavior:
 * - Only props passed in are applied; others default to 0
 * - Per-side props override shorthand
 * - Responsive props override base props at the specified breakpoints
 */
const BREAKPOINTS = ['', 'Md', 'Lg']; // Corresponds to base, tablet, desktop
const SPACING_PROPS = [
  'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
  'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
];

export default function AccountUniversalWrapper({ children, className = '', ...props }) {
  /**
   * Memoized styleVars object:
   * - Iterates through all breakpoints and spacing props using .forEach.
   * - For each prop, if a value is provided, sets the corresponding CSS variable.
   * - Example: paddingTopMd -> --padding-top-md
   */
  const styleVars = useMemo(() => {
    const vars = {};

    // Iterate through each breakpoint (base, Md, Lg, etc.)
    BREAKPOINTS.forEach((bp) => {
      // Determine the CSS variable suffix for the breakpoint (e.g., "-md")
      const cssSuffix = bp ? `-${bp.toLowerCase()}` : '';

      // Iterate through each spacing prop (e.g., paddingTop, marginLeft)
      SPACING_PROPS.forEach((prop) => {
        // Convert camelCase prop to kebab-case CSS variable (e.g., paddingTopMd -> --padding-top-md)
        const cssVarName = `${prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}${cssSuffix}`;
        // Construct the prop name as it would be passed in (e.g., paddingTopMd)
        const propName = `${prop}${bp}`;

        // If the prop was provided and is not empty, set the CSS variable
        if (props[propName] != null && props[propName] !== '') {
          vars[`--${cssVarName}`] = props[propName];
        }
      });
    });

    // Return the object containing all CSS variables to be applied inline
    return vars;
  }, [props]);

  return (
    <div
      className={`account-universal-wrapper${className ? ` ${className}` : ''}`} // Always has base class, adds extra if provided
      style={styleVars} // Inline CSS variables for dynamic spacing
      data-testid="account-universal-wrapper" // For testing purposes
    >
      {/* Render content inside wrapper */}
      {children}
    </div>
  );
}

AccountUniversalWrapper.propTypes = accountUniversalWrapperPropTypes;
AccountUniversalWrapper.defaultProps = accountUniversalWrapperDefaultProps;
